import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { AvForm, AvGroup, AvField } from "availity-reactstrap-validation";
import { Col, Label } from "reactstrap";
import { isNullOrUndefined } from "utils/validations";
import { initialPaginationFieldConfig } from "utils/initialPaginationsConfig";
import { showAlertServiceError } from "utils/alerts";
import { enumsListStatus, HttpStatus } from "utils/enums";
import { useTranslation } from "react-i18next";
import { useFieldsRecordContext } from "contextAPI/FieldsRecordContext";
import cx from "classnames";
import useList from "hooks/useList";

const ComboBoxComposed = (props) => {
  const { t } = useTranslation();
  const {
    id,
    label,
    hide,
    required,
    isPreview,
    isFilterForm,
    itemLink,
    handleOnChangeRecord,
    value,
    parentFieldUuid,
  } = props;
  const [pagination] = useState(initialPaginationFieldConfig);
  const [itemsByList, setItemsByList] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const { recordForm } = useFieldsRecordContext();
  const { getItems } = useList();
  const hasParentDependency = isNullOrUndefined(parentFieldUuid) === false;
  let hideLabelComboBox = null;
  let nameItemValue = "";
  let loadingComboBox = null;
  let selectedParentValue = "";
  let defaultOptionLabel = loadingComboBox;

  /**
   * Normalizes a field value into a string.
   * - Returns an empty string if the value is null or undefined.
   * - If the value is an object, it returns its `uuid` if present.
   * - Otherwise, it converts the value to a string.
   * @param {*} fieldValue - The value to normalize. Can be of any type.
   * @returns {string} A normalized string representation of the value.
   */
  const normalizeValue = (fieldValue) => {
    if (isNullOrUndefined(fieldValue) === true) {
      return "";
    }
    if (typeof fieldValue === "object") {
      if (isNullOrUndefined(fieldValue.uuid) === false) {
        return String(fieldValue.uuid);
      }
      return "";
    }

    return String(fieldValue);
  };

  /**
   * Sets the selected parent value when a parent dependency exists.
   * @param {boolean} hasParentDependency - Indicates whether the field depends on a parent.
   * @param {Object} recordForm - Form data object containing field values.
   * @param {string} parentFieldUuid - Key used to access the parent field value in the form.
   * @param {Function} normalizeValue - Function used to normalize the parent field value.
   */
  if (hasParentDependency === true) {
    selectedParentValue = normalizeValue(recordForm?.[parentFieldUuid]);
  }

  /**
   * Updates the 'nameItemValue' based on the provided 'value' object.
   * This function checks if the 'value' object is defined and if its 'name' property is not null.
   * If both conditions are met, it assigns the value of 'value.uuid' to 'nameItemValue'.
   * @param {object} value - The object to extract the 'uuid' property from.
   * @param {string} nameItemValue - The variable to be updated with the 'uuid' value if conditions are met.
   * @returns {void}
   */
  if (recordForm && isNullOrUndefined(recordForm[id]) === false) {
    nameItemValue = recordForm[id];
  } else if (isNullOrUndefined(value) === false && isNullOrUndefined(value.name) === false) {
    nameItemValue = value.uuid;
  }

  /**
   * Normalizes the current value of the name item.
   * @param {*} nameItemValue - The value to be normalized.
   * @returns {*} The normalized value.
   */
  const normalizedCurrentValue = normalizeValue(nameItemValue);

  /**
   * Emits a value change event only if the normalized value has changed.
   * @param {*} nextValue - The next value to evaluate and potentially emit.
   */
  const emitValueChangeIfNeeded = (nextValue) => {
    if (normalizeValue(nextValue) !== normalizedCurrentValue) {
      handleOnChangeRecord(id, nextValue);
    }
  };

  /**
   * Checks whether an item matches a given parent value.
   * It validates against multiple possible parent key fields and
   * also checks a nested parent object if present.
   * @param {Object} item - The item to evaluate.
   * @param {string|number|null|undefined} parentValue - The parent identifier to match against.
   * @returns {boolean} Returns true if the item matches the parent value, otherwise false.
   */
  const hasParentMatch = (item, parentValue) => {
    if (isNullOrUndefined(parentValue) === true || parentValue === "") {
      return false;
    }
    const directParentKeys = ["parent_id", "parent_uuid", "parent_item_uuid", "parentItemUuid"];
    const hasDirectParentMatch = directParentKeys.some(
      (key) => String(item[key]) === String(parentValue),
    );
    if (hasDirectParentMatch === true) {
      return true;
    }
    if (item.parent && String(item.parent.uuid) === String(parentValue)) {
      return true;
    }

    return false;
  };

  /**
   * Returns a filtered list of items based on parent dependency.
   * If there is no parent dependency, it returns all items.
   * @returns {Array} Filtered or complete list of items.
   */
  const getFilteredItems = () => {
    if (hasParentDependency === false) {
      return itemsByList;
    }
    return itemsByList.filter((item) => hasParentMatch(item, selectedParentValue));
  };

  const filteredItems = getFilteredItems();
  const isParentSelected = selectedParentValue !== "";
  const hasNoItemsAvailable = filteredItems.length === 0;
  const isDisabledByParent = hasParentDependency === true && isParentSelected === false;
  const isDisabledByNoItems =
    hasNoItemsAvailable === true && isParentSelected === true && isLoadingItems === false;
  const isDisabledField = isDisabledByParent === true || isDisabledByNoItems === true;

  /**
   * Handles the change event for a combo box and updates the record with the new value.
   * @param {Event} eventChangeComboBox - The change event triggered by the combo box.
   */
  const handleChangeComboBox = (eventChangeComboBox) => {
    emitValueChangeIfNeeded(eventChangeComboBox.target.value);
  };

  /**
   * Generate an array of option elements based on the status of items in the provided list.
   * @param {Array} itemsByList - The list of items to generate options from.
   * @returns {Array} An array of JSX option elements.
   */
  const generateOptions = (itemsByList) => {
    return itemsByList.map((item) => {
      if (item.status === enumsListStatus.ACTIVE && isFilterForm === false) {
        return (
          <option id={item.uuid} key={item.uuid} value={item.uuid}>
            {item.name}
          </option>
        );
      }

      if (isFilterForm === true) {
        return (
          <option id={item.uuid} key={item.uuid} value={item.name}>
            {item.name}
          </option>
        );
      }

      return null;
    });
  };

  /**
   * Fetches items using the provided item link and updates the list based on specified parameters.
   * @param {string} itemLink - The link or identifier for the items to be fetched.
   * @param {number} pageNumber - The page number of items to retrieve.
   * @param {number} perPage - The number of items to fetch per page.
   * @param {string} searchQuery - Optional search query to filter items (can be an empty string).
   * @returns {void}
   */
  useEffect(() => {
    if (isPreview === false && hide === true) return;
    const { per_page } = pagination;
    setIsLoadingItems(true);
    getItems(itemLink, 1, per_page, "")
      .then((response) => {
        const itemsList = response.data.items;
        if (isNullOrUndefined(itemsList) === false) {
          setItemsByList(itemsList);
        } else {
          showAlertServiceError();
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === HttpStatus.UNAUTHORIZED) {
          return;
        }
      })
      .finally(() => {
        setIsLoadingItems(false);
      });
  }, [itemLink, hide, pagination]);

  /**
   * Effect that validates the current selected value against the filtered items.
   * - Skips execution if there is no parent dependency.
   * - Skips if the field is disabled due to no available items.
   * - Skips if there is no selected value.
   * - If the current value does not exist in the filtered list, it resets the value.
   * @param {Array<Object>} filteredItems - List of available items to validate against.
   * @param {Function} emitValueChangeIfNeeded - Function used to update/reset the value.
   * @param {boolean} hasParentDependency - Indicates if the field depends on a parent value.
   * @param {string|number} id - Identifier of the field (used as dependency).
   * @param {boolean} isDisabledByNoItems - Flag indicating if the field is disabled due to no items.
   * @param {string|number} nameItemValue - Current selected value to validate.
   */
  useEffect(() => {
    if (hasParentDependency === false) {
      return;
    }
    if (isDisabledByNoItems === true) {
      return;
    }
    if (nameItemValue === "") {
      return;
    }
    const currentValueExists = filteredItems.some(
      (item) => String(item.uuid) === String(nameItemValue),
    );
    if (currentValueExists === false) {
      emitValueChangeIfNeeded("");
    }
  }, [
    filteredItems,
    emitValueChangeIfNeeded,
    hasParentDependency,
    id,
    isDisabledByNoItems,
    nameItemValue,
  ]);

  /**
   * Sets the default option label when the field is disabled by its parent.
   * @param {boolean} isDisabledByParent - Indicates if the field is disabled due to parent dependency.
   * @param {Function} t - Translation function used to get localized text.
   */
  if (isDisabledByParent === true) {
    defaultOptionLabel = t("form.composedFieldSelectParent");
  }

  /**
   * Sets the default option label when there are no items available.
   * @param {boolean} isDisabledByNoItems - Indicates if the field is disabled due to no available items.
   * @param {Function} t - Translation function used to get localized text.
   */
  if (isDisabledByNoItems === true) {
    defaultOptionLabel = t("form.noItemsToFill");
  }

  /**
   * Sets the `loadingComboBox` content based on the `isLoadingItems` flag.
   * @param {boolean} isLoadingItems - A flag indicating whether items are currently being loaded.
   * @returns {string} The localized text to be displayed in the combo box.
   */
  if (isLoadingItems === true) {
    loadingComboBox = t("loading");
  } else {
    loadingComboBox = t("form.designerLabel10.1");
  }

  /**
   * Conditionally generates a JSX element to hide or show a labeled comboBox.
   * @param {boolean} hide - A boolean flag indicating whether to hide the labeled comboBox.
   * @param {string} t - A function for translating text labels.
   * @returns {JSX.Element|null} - A JSX element representing a labeled comboBox with optional hiding text, or null if 'hide' is false.
   */
  if (hide === true) {
    hideLabelComboBox = <i className="hide-textBox custom-opacity">({t("form.field49")})</i>;
  } else {
    hideLabelComboBox = null;
  }

  /**
   * Renders a form with a select field conditionally based on the preview, hide, and filter form status.
   * @param {boolean} isPreview - Determines if the component is in preview mode.
   * @param {boolean} hide - Determines if the component should be hidden.
   * @param {boolean} isFilterForm - Determines if the component is part of a filter form.
   * @param {string} id - The unique identifier for the select field.
   * @param {string} label - The label text for the select field.
   * @param {boolean} required - Determines if the select field is required.
   * @param {Function} handleChangeComboBox - The function to handle change events for the select field.
   * @param {Function} handleOnChangeRecord - The function to handle change events for filter forms.
   * @param {JSX.Element} hideLabelComboBox - The element to display when in preview mode.
   * @param {Array} itemsByList - The list of items to generate options for the select field.
   * @param {string} nameItemValue - The current value of the select field.
   * @param {Function} generateOptions - The function to generate options for the select field.
   * @param {string} loadingComboBox - The loading text for the combo box.
   * @param {Function} i18n - The internationalization function for translating text.
   * @param {string} isRequired - The class name for required fields.
   * @returns {JSX.Element|null} The rendered form with the select field or null if hidden.
   */
  if (isPreview === true) {
    return (
      <AvForm>
        <AvGroup row>
          <Label
            className={cx("", {
              "is-required": required === true,
            })}
            sm={3}
          >
            {label} {hideLabelComboBox}
          </Label>
          <Col md={8}>
            <AvField
              id={id}
              name={id}
              type="select"
              key={id}
              required={required}
              disabled={isDisabledField}
              onChange={handleChangeComboBox}
              errorMessage={t("form.field46")}
              value={nameItemValue}
            >
              <option value="">{defaultOptionLabel}</option>
              {generateOptions(filteredItems)}
            </AvField>
          </Col>
        </AvGroup>
      </AvForm>
    );
  } else if (hide === false && isFilterForm === false) {
    return (
      <AvGroup row>
        <Label
          className={cx("", {
            "is-required": required === true,
          })}
          sm={3}
        >
          {label}
        </Label>
        <Col md={8}>
          <AvField
            id={id}
            name={id}
            type="select"
            key={id}
            required={required}
            disabled={isDisabledField}
            onChange={handleChangeComboBox}
            errorMessage={t("form.field46")}
            value={nameItemValue}
          >
            <option value="">{defaultOptionLabel}</option>
            {generateOptions(filteredItems)}
          </AvField>
        </Col>
      </AvGroup>
    );
  } else if (isFilterForm === true) {
    return (
      <Col md={12}>
        <AvField
          key={id}
          id={id}
          name={id}
          type="select"
          required={required}
          disabled={isDisabledField}
          onChange={handleOnChangeRecord}
          errorMessage={t("form.field46")}
          value={nameItemValue}
        >
          <option value="">{defaultOptionLabel}</option>
          {generateOptions(filteredItems)}
        </AvField>
      </Col>
    );
  } else {
    return null;
  }
};

ComboBoxComposed.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hide: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool.isRequired,
  isFilterForm: PropTypes.bool,
  itemLink: PropTypes.string.isRequired,
  handleOnChangeRecord: PropTypes.func.isRequired,
  value: PropTypes.object,
  childFieldUuid: PropTypes.string,
  parentFieldUuid: PropTypes.string,
};

export default React.memo(ComboBoxComposed);
